/**
 * M9 CH-2a: seeded First Rush hulk placement (plan `docs/plans/2026-10-05-m9-claimable-hulls.md` §1 H-1, H-9).
 *
 * Pure and deterministic per galaxy seed: each system gets 0–n hulk points, drawn by `hash32(seed, system, n, SALT)`
 * (a local stream seeded from that hash, never a shared RNG, MS-2). The points sit in open space like the M7 story
 * sites (`story-link/sites.ts placeSite`): outside a seeded body's mass-lock shell, but much closer in
 * (`hulks.json offsetM`), and at least `clearanceM` from every station, relay, lane ring, POI (so never on a
 * derelict), story site and other hulk. A hulk whose `tries` candidates all fail is dropped, never forced.
 * None in the gate region, the home system or the story sites' systems (`excludeSystems`); the rim has the most.
 *
 * The list is finite: callers subtract `career.hulks.done` (CH-2c), so a claimed or scrapped hulk never returns.
 * The M3 layout is only read; no galaxy golden moves. Event-driven (system entry), never per frame; it allocates.
 */
import type { QuatT, SystemLayout, Vec3T } from '../galaxy/layout/types'
import type { Galaxy, RegionId } from '../galaxy/types'
import { hash32, hashString } from '../space/hash'
import { siteClearance, type StorySite, type StorySites } from '../story-link/sites'
import { hulksConfig, placementOf, type HulkExclusion, type HulksConfig } from './data'

/** Salts for the hulk draws (fixed forever: changing one moves every hulk in every galaxy). */
const SALT_HULK_COUNT = 0x48554c4b // 'HULK'
const SALT_HULK_SPOT = 0x48534f54
const SALT_HULK_SEED = 0x48534544

/** One seeded hulk point. */
export interface HulkSpot {
  /** Stable key `hk:<systemId>:<n>` (the career's hulk key, CH-2b). */
  readonly key: string
  readonly systemId: string
  /** Strategic `hulls.json` id. */
  readonly hull: string
  /** System frame, float64 metres (rebase before rendering, D-013). */
  readonly position: Vec3T
  readonly rotation: QuatT
  /** The hulk's own seed (CH-4 rolls its flaws from `hash(seed, SALT)`). */
  readonly seed: number
}

/** mulberry32 over a 32-bit seed: a stream local to one hulk, never shared. */
function stream(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function dist(a: Vec3T, b: Vec3T): number {
  const dx = a[0] - b[0]
  const dy = a[1] - b[1]
  const dz = a[2] - b[2]
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

/** A uniform random unit quaternion (Shoemake) from three uniforms. */
function randomQuat(u1: number, u2: number, u3: number): QuatT {
  const a = Math.sqrt(1 - u1)
  const b = Math.sqrt(u1)
  const t2 = 2 * Math.PI * u2
  const t3 = 2 * Math.PI * u3
  return [a * Math.sin(t2), a * Math.cos(t2), b * Math.sin(t3), b * Math.cos(t3)]
}

/** The system ids an exclusion names. */
function excludedSystemIds(galaxy: Galaxy, sites: StorySites, exclude: readonly HulkExclusion[]): Set<string> {
  const ids = new Set<string>()
  for (const e of exclude) {
    if (e === 'home') ids.add(galaxy.homeSystemId)
    else if (e === 'columnNine') ids.add(sites.columnNine.systemId)
    else ids.add(sites.okaforLab.systemId)
  }
  return ids
}

/** How many hulk points a system of `region` draws (before any is dropped for clearance). */
export function hulkCountOf(seed: number, systemId: string, region: RegionId, cfg: HulksConfig = hulksConfig()): number {
  const [min, max] = cfg.perSystem[region]
  return min + (hash32(seed, hashString(systemId), SALT_HULK_COUNT) % (max - min + 1))
}

/**
 * The hull drawn for a region from a uniform `u` in [0, 1) by placement table `table` (M10 P3b1: `hulks.json
 * placementTables`, walked in key order); null when no hull has weight there. Throws for a table this build lacks.
 */
export function hulkHullOf(region: RegionId, u: number, table: number, cfg: HulksConfig = hulksConfig()): string | null {
  const entries = Object.entries(placementOf(cfg, table).eligibleHulls)
  let total = 0
  for (const [, w] of entries) total += w[region]
  if (total <= 0) return null
  let pick = u * total
  for (const [id, w] of entries) {
    if (w[region] <= 0) continue
    pick -= w[region]
    if (pick < 0) return id
  }
  for (let i = entries.length - 1; i >= 0; i--) if (entries[i][1][region] > 0) return entries[i][0]
  return null
}

/**
 * The nearest distance from `p` to a structure (station, relay, lane ring, POI), a story site of this system (its
 * edge) or an already placed hulk, and whether `p` is outside every body's shell.
 */
export function hulkClearance(layout: SystemLayout, sites: StorySites, placed: readonly HulkSpot[], p: Vec3T): { clearM: number; outsideBodies: boolean } {
  const c = siteClearance(layout, p)
  let min = c.structuresM
  const story: readonly StorySite[] = [sites.columnNine, sites.okaforLab]
  for (const s of story) if (s.systemId === layout.systemId) min = Math.min(min, dist(p, s.position) - s.radiusM)
  for (const h of placed) min = Math.min(min, dist(p, h.position))
  return { clearM: min, outsideBodies: c.outsideBodies }
}

/**
 * The seeded hulks of `layout`'s system: deterministic per galaxy seed, before subtracting the career's done keys.
 * `sites` is the M7 overlay (`storySites`) of the same galaxy; `table` is the galaxy's placement table (M10 P3b1;
 * callers pass `hulkTableOf(career)`, P3b2). Throws for a table this build lacks.
 */
export function hulksOf(galaxy: Galaxy, layout: SystemLayout, sites: StorySites, table: number, cfg: HulksConfig = hulksConfig()): HulkSpot[] {
  placementOf(cfg, table) // an unknown table throws, even where no hulk is drawn
  if (!cfg.enabled) return []
  if (excludedSystemIds(galaxy, sites, cfg.excludeSystems).has(layout.systemId)) return []
  const sysHash = hashString(layout.systemId)
  const count = hulkCountOf(galaxy.seed, layout.systemId, layout.region, cfg)
  const out: HulkSpot[] = []
  for (let n = 0; n < count; n++) {
    const rnd = stream(hash32(galaxy.seed, sysHash, n, SALT_HULK_SPOT))
    const hull = hulkHullOf(layout.region, rnd(), table, cfg)
    if (hull === null) break
    const rotation = randomQuat(rnd(), rnd(), rnd())
    let position: Vec3T | null = null
    for (let i = 0; i < cfg.tries && position === null; i++) {
      const body = layout.bodies.length > 0 ? layout.bodies[Math.floor(rnd() * layout.bodies.length)] : undefined
      const origin: Vec3T = body?.position ?? [0, 0, 0]
      const base = body?.shellM ?? layout.star.radiusM * 3
      const r = base + cfg.offsetM[0] + rnd() * (cfg.offsetM[1] - cfg.offsetM[0])
      const theta = rnd() * Math.PI * 2
      // Near the disc plane (x–z, y up): a small height off it, like the M3 bodies.
      const yf = (rnd() - 0.5) * 0.2
      const xz = Math.sqrt(1 - yf * yf)
      const p: Vec3T = [origin[0] + r * xz * Math.cos(theta), origin[1] + r * yf, origin[2] + r * xz * Math.sin(theta)]
      const c = hulkClearance(layout, sites, out, p)
      if (c.outsideBodies && c.clearM >= cfg.clearanceM) position = p
    }
    if (position === null) continue
    out.push({
      key: `hk:${layout.systemId}:${n}`,
      systemId: layout.systemId,
      hull,
      position,
      rotation,
      seed: hash32(galaxy.seed, sysHash, n, SALT_HULK_SEED),
    })
  }
  return out
}
